import { useEffect, useMemo, useRef, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { VIEWS, VIEW_LABELS, faceAspect } from "./data/collections";
import { CATEGORY_LABELS, pick, provinceName, useLang } from "./i18n";

const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';
const CROP_SCALE = 2048; // cropBoxes ใน manifest วัดที่ภาพกางกว้าง 2048px

export default function ZoomModal({ collection, target, onTargetChange, onClose, onHotspotOpen }) {
  const { lang, t } = useLang();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const canvasRef = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [natural, setNatural] = useState(null); // ขนาดจริงของภาพกาง (ใช้ตอนแท็บ Artwork ต้นฉบับ)
  const [scale, setScale] = useState(1);
  const [openId, setOpenId] = useState(null);

  // focus trap + Esc + ล็อก scroll หน้า แล้วคืน focus ปุ่มเดิมเมื่อปิด
  useEffect(() => {
    const previous = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const items = [...dialogRef.current.querySelectorAll(FOCUSABLE)];
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
      if (previous && previous.focus) previous.focus();
    };
  }, [onClose]);

  // ขนาดพื้นที่แสดงภาพ เพื่อวางกรอบภาพให้พอดี (จุดเด่นอ้างอิงตำแหน่งบนกรอบนี้)
  useEffect(() => {
    const el = canvasRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => setBox({ w: entry.contentRect.width, h: entry.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setOpenId(null);
    setScale(1);
  }, [target]);

  const isArtwork = target === "artwork";
  const src = isArtwork ? collection.artworkSource : collection.textures[target];
  const caption = isArtwork ? t("originalArtworkCaption") : `${VIEW_LABELS[target][lang]}`;
  const aspect = isArtwork ? (natural ? natural.h / natural.w : null) : faceAspect(collection, target);

  const frame = useMemo(() => {
    if (!aspect || !box.w) return null;
    const availW = box.w - 32;
    const availH = box.h - 72; // เว้นที่ให้ปุ่มซูมด้านล่าง
    const w = Math.max(0, Math.min(availW, availH / aspect));
    return { w, h: w * aspect };
  }, [aspect, box]);

  // ตำแหน่งจุดเด่นบนภาพ (0–1): ด้านเดียวใช้ u,v ตรง ๆ / ภาพกางแปลงผ่านกรอบตัดของแต่ละด้าน
  const points = useMemo(() => {
    if (!isArtwork) return collection.hotspots.filter((h) => h.view === target).map((h) => ({ h, x: h.u, y: h.v }));
    if (!natural || !collection.cropBoxes) return [];
    const fullH = (CROP_SCALE * natural.h) / natural.w;
    return collection.hotspots
      .filter((h) => collection.cropBoxes[h.view])
      .map((h) => {
        const [x0, y0, x1, y1] = collection.cropBoxes[h.view];
        return { h, x: (x0 + h.u * (x1 - x0)) / CROP_SCALE, y: (y0 + h.v * (y1 - y0)) / fullH };
      });
  }, [collection, target, isArtwork, natural]);

  const toggle = (h) => {
    const next = openId === h.id ? null : h.id;
    setOpenId(next);
    if (next) onHotspotOpen(h);
  };

  return (
    <div
      className="bce-modal"
      role="dialog"
      aria-modal="true"
      aria-label={t("zoomDialog", provinceName(collection, lang))}
      ref={dialogRef}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bce-modal-bar">
        <div className="bce-modal-tabs" role="group" aria-label={t("zoomTabs")}>
          {VIEWS.map((view) => (
            <button key={view} type="button" className={target === view ? "is-selected" : ""} aria-pressed={target === view} onClick={() => onTargetChange(view)}>
              {VIEW_LABELS[view][lang]}
            </button>
          ))}
          <button type="button" className={isArtwork ? "is-selected" : ""} aria-pressed={isArtwork} onClick={() => onTargetChange("artwork")}>
            {t("originalArtwork")}
          </button>
        </div>
        <button type="button" ref={closeRef} className="bce-modal-close" onClick={onClose}>
          {t("close")}
        </button>
      </div>

      <div className="bce-modal-canvas-wrap" ref={canvasRef}>
        <TransformWrapper
          key={src}
          minScale={1}
          maxScale={6}
          centerOnInit
          wheel={{ step: 0.15 }}
          doubleClick={{ disabled: true }}
          panning={{ excluded: ["bce-zoom-hotspot", "bce-zoom-tip"] }}
          onTransformed={(_, s) => setScale(s.scale)}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <TransformComponent wrapperClass="bce-modal-canvas" wrapperStyle={{ width: "100%", height: "100%" }} contentStyle={{ width: "100%", height: "100%" }}>
                <div className="bce-zoom-stage" onClick={() => setOpenId(null)}>
                  <div className="bce-zoom-frame" style={frame ? { width: frame.w, height: frame.h } : { width: "90%" }}>
                    <img
                      className="bce-modal-img"
                      src={src}
                      alt={`${caption} — ${provinceName(collection, lang)}`}
                      draggable={false}
                      onLoad={(e) => isArtwork && setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                    />
                    {frame &&
                      points.map(({ h, x, y }) => {
                        const open = openId === h.id;
                        const place = `${y < 0.3 ? "below" : "above"} ${x < 0.35 ? "start" : x > 0.65 ? "end" : "mid"}`;
                        return (
                          <div
                            key={h.id}
                            className={`bce-zoom-point${open ? " is-open" : ""}`}
                            style={{ left: `${x * 100}%`, top: `${y * 100}%`, transform: `translate(-50%, -50%) scale(${1 / scale})` }}
                          >
                            <button
                              type="button"
                              className={`bce-zoom-hotspot${h.category ? ` bce-cat--${h.category}` : ""}`}
                              aria-expanded={open}
                              aria-label={t("hotspotLabel", pick(h, "title", lang))}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggle(h);
                              }}
                            >
                              <span aria-hidden="true">+</span>
                            </button>
                            {open && (
                              <div className={`bce-zoom-tip ${place}`} role="tooltip" onClick={(e) => e.stopPropagation()}>
                                {h.category && <span className={`bce-zoom-tip-cat bce-cat--${h.category}`}>{CATEGORY_LABELS[h.category][lang]}</span>}
                                <b>{pick(h, "title", lang)}</b>
                                <p>{pick(h, "description", lang)}</p>
                                <button type="button" className="bce-zoom-tip-close" aria-label={t("closeTip")} onClick={() => setOpenId(null)}>
                                  ×
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              </TransformComponent>
              <div className="bce-modal-tools">
                <button type="button" aria-label={t("zoomOut")} onClick={() => zoomOut()}>
                  −
                </button>
                <button type="button" aria-label={t("zoomResetLabel")} onClick={() => resetTransform()}>
                  {t("zoomReset")}
                </button>
                <button type="button" aria-label={t("zoomIn")} onClick={() => zoomIn()}>
                  ＋
                </button>
              </div>
            </>
          )}
        </TransformWrapper>
      </div>
      <p className="bce-modal-caption">
        {provinceName(collection, lang)} · {caption} — {t("zoomHelp")}
      </p>
    </div>
  );
}
